import React, { useState, useRef, useEffect } from 'react';
import { Bell, Music, Clock, AlertCircle, Flag, Volume2, VolumeX } from 'lucide-react';
import { useNotifications } from '../../Context/NotificationContext';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import './NotificationBell.scss';
import { formatNotificationCount } from '../../utils/formatNotificationCount';

// Extend dayjs with relativeTime plugin for "time ago" formatting
dayjs.extend(relativeTime);

const NotificationBell = () => {
  const { notifications, unreadCount, markAsRead, markAllAsRead, isConnected } = useNotifications();
  const [isOpen, setIsOpen] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true); // Default to enabled
  const dropdownRef = useRef(null);
  const prevUnreadCountRef = useRef(unreadCount);

  // Load sound preference from localStorage on component mount
  useEffect(() => {
    const savedSoundPreference = localStorage.getItem('artistNotificationSoundEnabled');
    if (savedSoundPreference !== null) {
      setSoundEnabled(JSON.parse(savedSoundPreference));
    }
  }, []);

  // Watch for new notifications and play sound if enabled
  useEffect(() => {
    // Only play sound if unread count increased (new notification arrived)
    if (unreadCount > prevUnreadCountRef.current && soundEnabled && isConnected) {
      playNotificationSound();
    }
    prevUnreadCountRef.current = unreadCount;
  }, [unreadCount, soundEnabled, isConnected]);

  // Function to play notification sound
  const playNotificationSound = () => {
    try {
      // Create audio context and generate a pleasant notification sound
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();

      // Create a sequence of tones for a pleasant notification sound
      const playTone = (frequency, startTime, duration) => {
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();

        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);

        oscillator.frequency.value = frequency;
        oscillator.type = 'sine';

        gainNode.gain.setValueAtTime(0, startTime);
        gainNode.gain.linearRampToValueAtTime(0.1, startTime + 0.01);
        gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + duration);

        oscillator.start(startTime);
        oscillator.stop(startTime + duration);
      };

      const now = audioContext.currentTime;
      // Play a pleasant two-tone notification sound
      playTone(800, now, 0.15);
      playTone(1000, now + 0.15, 0.15);
    } catch (error) {
      // Audio playback is optional; ignore failures
    }
  };

  // Handle sound toggle change
  const handleSoundToggle = () => {
    const newSoundEnabled = !soundEnabled;
    setSoundEnabled(newSoundEnabled);
    localStorage.setItem('artistNotificationSoundEnabled', JSON.stringify(newSoundEnabled));

    // Play a test sound when enabling
    if (newSoundEnabled) {
      playNotificationSound();
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = event => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Toggle dropdown
  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  // Handle notification click
  const handleNotificationClick = notificationId => {
    markAsRead(notificationId);
  };

  // Get notification icon based on notification type
  const getNotificationIcon = notificationType => {
    switch (notificationType) {
      case 'playlist_added':
        return { Icon: Music, className: 'type-music' };
      case 'campaign_ending_one_day':
        return { Icon: Clock, className: 'type-warning' };
      case 'campaign_ending_five_minutes':
        return { Icon: AlertCircle, className: 'type-urgent' };
      case 'campaign_ended':
        return { Icon: Flag, className: 'type-ended' };
      default:
        return { Icon: Bell, className: 'type-bell' };
    }
  };

  return (
    <div className="notification-bell-container" ref={dropdownRef}>
      <button
        type="button"
        className={`notification-bell ${!isConnected ? 'disconnected' : ''}`}
        onClick={toggleDropdown}
        aria-expanded={isOpen}
        aria-haspopup="true"
        aria-label={
          isConnected
            ? `Notifications${unreadCount > 0 ? `, ${unreadCount} unread` : ''}`
            : 'Notification system disconnected'
        }
      >
        <Bell size={20} aria-hidden />
        {unreadCount > 0 && (
          <span className="notification-count">{formatNotificationCount(unreadCount)}</span>
        )}
      </button>

      {isOpen && (
        <div className="notification-dropdown">
          <div className="notification-header">
            <h3>Notifications</h3>
            <div className="notification-actions">
              <div className="sound-toggle-container">
                <label className="sound-toggle">
                  <input type="checkbox" checked={soundEnabled} onChange={handleSoundToggle} />
                  <span className="sound-toggle-slider"></span>
                  <span className="soundIcon">
                    {soundEnabled ? <Volume2 size={14} /> : <VolumeX size={14} />}
                  </span>
                </label>
              </div>
              {unreadCount > 0 && (
                <button className="mark-all-read" onClick={markAllAsRead}>
                  Mark all as read
                </button>
              )}
            </div>
          </div>

          <div className="notification-list">
            {notifications.length === 0 ? (
              <div className="no-notifications">
                {isConnected
                  ? 'No notifications yet.'
                  : 'Unable to connect to notification server. Please refresh the page.'}
              </div>
            ) : (
              notifications.map(notification => {
                const { Icon: TypeIcon, className: typeIconClassName } = getNotificationIcon(
                  notification?.notificationType
                );
                return (
                  <div
                    key={notification?._id || Math.random().toString()}
                    className={`notification-item ${!notification?.isRead ? 'unread' : ''} ${
                      notification?.notificationType === 'campaign_ended' ? 'campaign-ended' : ''
                    }`}
                    onClick={() =>
                      notification?._id &&
                      !notification?.isRead &&
                      handleNotificationClick(notification._id)
                    }
                  >
                    <div className="notification-body">
                      <div className="notification-time">
                        <span className={`notification-type-icon ${typeIconClassName}`}>
                          <TypeIcon size={12} />
                        </span>
                        {notification?.created_at
                          ? dayjs(notification.created_at).fromNow()
                          : 'Just now'}
                      </div>
                      <div className="notification-header-row">
                        <div className="notification-message">
                          {notification?.message || 'New notification'}
                        </div>
                      </div>
                      {notification?.campaignData && (
                        <div className="notification-campaign">
                          <div className="campaign-info">
                            <span className="campaign-icon">
                              <Music size={16} />
                            </span>
                            <div className="campaign-details">
                              <span className="campaign-name">
                                {notification.campaignData.track_name}
                              </span>
                              <span className="campaign-artist">
                                {notification.campaignData.artist_name}
                              </span>
                            </div>
                          </div>
                        </div>
                      )}
                      <div className="notification-footer">
                        <div
                          className="notification-status-badge"
                          data-status={notification?.status}
                        >
                          {notification?.status === 'new' && 'Submitted'}
                          {notification?.status === 'in-progress' && 'In Progress'}
                          {notification?.status === 'in-review' && 'In Review'}
                          {notification?.status === 'completed' && 'Completed'}
                        </div>
                        {notification?.campaignData?.endTime && (
                          <div className="notification-end-date">
                            <Clock size={10} />
                            {dayjs(notification.campaignData.endTime).format('MMM D, YYYY')}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
